import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getGaPagePath, trackGaEvent } from "@/lib/ga4";

const DEFAULT_QUESTIONS = [
  {
    id: "priority_focus",
    question: "What matters more right now?",
    options: [
      { label: "Easier transition", country: "PT" },
      { label: "More lifestyle variety", country: "ES" },
    ],
  },
  {
    id: "main_concern",
    question: "What concerns you more?",
    options: [
      { label: "Residency simplicity", country: "PT" },
      { label: "Housing and cost pressure", country: "ES" },
    ],
  },
  {
    id: "healthcare_shape",
    question: "Which healthcare situation sounds better?",
    options: [
      { label: "Easier private-care access", country: "PT" },
      { label: "Broader regional system variety", country: "ES" },
    ],
  },
  {
    id: "daily_pace",
    question: "What daily pace sounds more appealing?",
    options: [
      { label: "Slower, softer landing", country: "PT" },
      { label: "More regional energy and movement", country: "ES" },
    ],
  },
  {
    id: "comfort_zone",
    question: "Which sounds closer to your comfort zone?",
    options: [
      { label: "English-friendly expat areas", country: "PT" },
      { label: "More local immersion and adaptation", country: "ES" },
    ],
  },
];

const DEFAULT_RESULT_CONTENT = {
  PT: {
    headline: "Portugal may deserve the first closer look",
    body: "Your answers lean toward Portugal because transition ease, residency simplicity, and a softer landing appear to matter more right now. Spain may still deserve a closer look if regional variety and broader system depth become more important over time.",
  },
  ES: {
    headline: "Spain may deserve the first closer look",
    body: "Your answers lean toward Spain because regional variety, broader system range, and deeper local immersion appear to matter more. Portugal may still deserve a closer look if simpler setup and an easier transition begin to carry more weight.",
  },
  balanced: {
    headline: "Your answers are more balanced — both countries likely deserve a deeper comparison",
    body: "Neither country clearly pulls ahead from these answers alone. The more useful next step is to weigh healthcare, residency practicality, budget pressure, lifestyle fit, and long-term tradeoffs together.",
  },
};

const collectCountryCodes = (questions, countryPriorityOrder = []) => {
  const codes = new Set(countryPriorityOrder);

  questions.forEach((question) => {
    question.options.forEach((option) => {
      if (option.scores) {
        Object.keys(option.scores).forEach((code) => codes.add(code));
        return;
      }

      if (option.country) {
        codes.add(option.country);
      }
    });
  });

  return Array.from(codes);
};

const defaultAlternativeNote = (leadName, closeAlternativeNames = []) => {
  if (!closeAlternativeNames.length) {
    return `This is a directional result, not a final recommendation. The full Relocation Clarity Assessment considers many additional factors that may still change the lead.`;
  }

  if (closeAlternativeNames.length === 1) {
    return `${closeAlternativeNames[0]} also came through as a strong match from the priorities you selected. The full Relocation Clarity Assessment considers many additional factors that may still change the final recommendation.`;
  }

  const allButLast = closeAlternativeNames.slice(0, -1).join(", ");
  const last = closeAlternativeNames[closeAlternativeNames.length - 1];
  return `${allButLast} and ${last} also came through as strong matches from the priorities you selected. The full Relocation Clarity Assessment considers many additional factors that may still change the final recommendation.`;
};

export default function PortugalSpainQuickFitCheck({
  rootTestId = "relocation-clarity-quick-fit",
  kicker = "Signature decision tool",
  title = "Relocation Clarity Quick Fit™",
  description = "Answer a few questions to see which options may best match your priorities.",
  quickFitType = "generic",
  questions = DEFAULT_QUESTIONS,
  resultContent = DEFAULT_RESULT_CONTENT,
  countryLabels = { PT: "Portugal", ES: "Spain" },
  countryPriorityOrder = ["PT", "ES"],
  closeThreshold = 1,
  maxCloseAlternatives = 2,
  buildResult,
  methodologyTitle = "Why did this result come out this way?",
  methodologyParagraph1 = "This Quick Fit™ reflects the priorities selected above. Different priorities can produce different recommendations because Relocation Clarity doesn’t rank countries—it evaluates how well each country fits what matters most to you.",
  methodologyParagraph2 = "This Quick Fit™ is a starting point. The full Relocation Clarity assessment evaluates multiple countries, weighs all of your priorities together, and explains why each recommendation fits your goals.",
}) {
  const [answers, setAnswers] = useState({});
  const hasTrackedStartRef = useRef(false);
  const hasTrackedCompleteRef = useRef(false);

  const countryCodes = useMemo(
    () => collectCountryCodes(questions, countryPriorityOrder),
    [countryPriorityOrder, questions]
  );

  const answeredCount = Object.keys(answers).length;
  const isComplete = answeredCount === questions.length;

  const result = useMemo(() => {
    if (!isComplete) return null;

    const scoreMap = Object.fromEntries(countryCodes.map((code) => [code, 0]));

    Object.values(answers).forEach((selectedOption) => {
      if (selectedOption?.scores) {
        Object.entries(selectedOption.scores).forEach(([code, value]) => {
          scoreMap[code] = (scoreMap[code] || 0) + value;
        });
        return;
      }

      if (selectedOption?.country) {
        scoreMap[selectedOption.country] = (scoreMap[selectedOption.country] || 0) + 1;
      }
    });

    const priorityIndex = Object.fromEntries(countryPriorityOrder.map((code, index) => [code, index]));
    const sortedScores = Object.entries(scoreMap)
      .map(([code, score]) => ({ code, score, name: countryLabels[code] || code }))
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        const aPriority = priorityIndex[a.code] ?? Number.MAX_SAFE_INTEGER;
        const bPriority = priorityIndex[b.code] ?? Number.MAX_SAFE_INTEGER;
        if (aPriority !== bPriority) return aPriority - bPriority;
        return a.name.localeCompare(b.name);
      });

    const lead = sortedScores[0];
    const closeAlternatives = sortedScores
      .slice(1)
      .filter((item) => item.score > 0 && lead.score - item.score <= closeThreshold)
      .slice(0, maxCloseAlternatives);

    const fallbackBalanced = resultContent.balanced || {
      headline: `${lead.name} may deserve the first closer look`,
      body: `Your answers lean toward ${lead.name} based on the priorities you selected.`,
    };

    let generatedResult;
    if (buildResult) {
      generatedResult = buildResult({
        lead,
        sortedScores,
        closeAlternatives,
        closeAlternativeNames: closeAlternatives.map((item) => item.name),
        answers,
      });
    } else if (resultContent[lead.code]) {
      generatedResult = resultContent[lead.code];
    } else {
      generatedResult = fallbackBalanced;
    }

    return {
      ...generatedResult,
      analytics: {
        lead_country: lead?.name || null,
        lead_country_code: lead?.code || null,
        close_alternatives_count: closeAlternatives.length,
      },
      supportingNote:
        generatedResult?.supportingNote ||
        defaultAlternativeNote(lead.name, closeAlternatives.map((item) => item.name)),
    };
  }, [answers, buildResult, closeThreshold, countryCodes, countryLabels, countryPriorityOrder, isComplete, maxCloseAlternatives, resultContent]);

  const handleAnswer = (questionId, option) => {
    if (!hasTrackedStartRef.current) {
      trackGaEvent("quick_fit_started", {
        quick_fit_type: quickFitType,
        page_path: getGaPagePath(),
        question_count: questions.length,
      });
      hasTrackedStartRef.current = true;
    }

    setAnswers((current) => ({ ...current, [questionId]: option }));
  };

  const resetAnswers = () => {
    hasTrackedStartRef.current = false;
    hasTrackedCompleteRef.current = false;
    setAnswers({});
  };

  useEffect(() => {
    if (!result || hasTrackedCompleteRef.current) return;

    trackGaEvent("quick_fit_completed", {
      quick_fit_type: quickFitType,
      page_path: getGaPagePath(),
      lead_country: result.analytics?.lead_country,
      lead_country_code: result.analytics?.lead_country_code,
      close_alternatives_count: result.analytics?.close_alternatives_count ?? 0,
      question_count: questions.length,
    });
    hasTrackedCompleteRef.current = true;
  }, [quickFitType, questions.length, result]);

  const handleAssessmentCtaClick = () => {
    trackGaEvent("quick_fit_cta_clicked", {
      quick_fit_type: quickFitType,
      page_path: getGaPagePath(),
      lead_country: result?.analytics?.lead_country,
      lead_country_code: result?.analytics?.lead_country_code,
      close_alternatives_count: result?.analytics?.close_alternatives_count ?? 0,
      cta_label: "Take the Assessment",
    });
  };

  return (
    <section
      className="rounded-[2.2rem] border border-stone-200/80 bg-[linear-gradient(135deg,rgba(251,249,244,0.98)_0%,rgba(255,252,247,0.99)_100%)] px-6 py-8 shadow-[0_24px_52px_rgba(28,25,23,0.07)] sm:px-8 sm:py-9 lg:px-10"
      data-testid={rootTestId}
    >
      <div className="max-w-4xl">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500" data-testid="fit-check-kicker">
          {kicker}
        </p>
        <h2 className="mt-3 font-manrope text-3xl text-stone-900" data-testid="fit-check-title">
          {title}
        </h2>
        <p className="mt-4 max-w-3xl text-base leading-8 text-stone-600" data-testid="fit-check-description">
          {description}
        </p>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-[1.4rem] border border-stone-200/80 bg-white/85 px-4 py-3" data-testid="fit-check-progress-row">
        <p className="text-sm font-medium text-stone-700" data-testid="fit-check-progress-text">
          {answeredCount} of {questions.length} answered
        </p>
        <button
          type="button"
          onClick={resetAnswers}
          className="inline-flex items-center gap-2 text-sm font-medium text-stone-600 transition-colors hover:text-stone-900"
          data-testid="fit-check-reset-button"
        >
          <RotateCcw className="h-4 w-4" />
          Reset
        </button>
      </div>

      <div className="mt-7 space-y-6" data-testid="fit-check-question-list">
        {questions.map((item, questionIndex) => (
          <article
            key={item.id}
            className="rounded-[1.8rem] border border-stone-200/80 bg-white px-5 py-6 shadow-[0_10px_30px_rgba(28,25,23,0.04)] sm:px-6"
            data-testid={`fit-check-question-${questionIndex + 1}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <h3 className="font-manrope text-[1.35rem] font-semibold leading-tight text-stone-950" data-testid={`fit-check-question-title-${questionIndex + 1}`}>
                {questionIndex + 1}. {item.question}
              </h3>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2" data-testid={`fit-check-options-${questionIndex + 1}`}>
              {item.options.map((option, optionIndex) => {
                const selectedValue = answers[item.id]?.country || answers[item.id]?.label;
                const optionValue = option.country || option.label;
                const isSelected = selectedValue === optionValue;
                return (
                  <button
                    key={option.label}
                    type="button"
                    onClick={() => handleAnswer(item.id, option)}
                    className={`min-h-[72px] rounded-[1.25rem] border px-4 py-4 text-left text-sm leading-7 transition-[border-color,background-color,transform,box-shadow] duration-200 hover:-translate-y-0.5 ${isSelected ? "border-amber-300 bg-[linear-gradient(135deg,rgba(252,237,206,0.98)_0%,rgba(255,246,228,1)_100%)] text-stone-950 shadow-[0_14px_28px_rgba(173,121,28,0.12)]" : "border-stone-200 bg-white text-stone-700 hover:border-stone-300 hover:bg-stone-50/80"}`}
                    data-testid={`fit-check-option-${questionIndex + 1}-${optionIndex + 1}`}
                    aria-pressed={isSelected}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </article>
        ))}
      </div>

      {result ? (
        <div className="mt-8 rounded-[1.8rem] border border-amber-300 bg-[linear-gradient(135deg,rgba(252,237,206,0.98)_0%,rgba(255,246,228,1)_100%)] px-6 py-7 text-stone-900 shadow-[0_14px_28px_rgba(173,121,28,0.12)] sm:px-7" data-testid="fit-check-result-panel">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500" data-testid="fit-check-result-kicker">
            Directional result
          </p>
          <h3 className="mt-3 font-manrope text-2xl text-stone-900" data-testid="fit-check-result-title">
            {result.headline}
          </h3>
          <p className="mt-4 max-w-3xl text-base leading-8 text-stone-700" data-testid="fit-check-result-copy">
            {result.body}
          </p>
          {result.supportingNote ? (
            <p className="mt-4 max-w-3xl text-sm leading-7 text-stone-700" data-testid="fit-check-result-supporting-note">
              {result.supportingNote}
            </p>
          ) : null}

          <div className="mt-7 rounded-[1.5rem] border border-stone-200/75 bg-white/82 px-5 py-5 shadow-[0_10px_30px_rgba(28,25,23,0.04)] sm:px-6" data-testid="fit-check-result-cta">
            <h4 className="font-manrope text-xl text-stone-900" data-testid="fit-check-result-cta-title">
              See how the full relocation picture changes when your priorities are weighted together.
            </h4>
            <div className="mt-4 max-w-3xl space-y-3 text-sm leading-7 text-stone-700" data-testid="fit-check-result-methodology-block">
              <p className="font-manrope text-lg leading-7 text-stone-900" data-testid="fit-check-result-methodology-title">
                {methodologyTitle}
              </p>
              <p data-testid="fit-check-result-methodology-paragraph-1">
                {methodologyParagraph1}
              </p>
              <p data-testid="fit-check-result-methodology-paragraph-2">
                {methodologyParagraph2}
              </p>
            </div>
            <div className="mt-5">
              <Button asChild className="rounded-full bg-white px-6 text-stone-900 hover:bg-amber-100" data-testid="fit-check-result-cta-button">
                <Link to="/auth" onClick={handleAssessmentCtaClick}>
                  Take the Assessment
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}